import * as React from 'react';
import { Input, type TextProps } from '@components/atoms';
import { Add, Deduct } from '@components/atoms/icons';
import { Button } from '@components/molecules/button/button';
import { cn, type Sizes } from '@utils';

const COUNTER_STEP = 1;

interface CounterProps extends React.HTMLAttributes<HTMLDivElement> {
	value: number;
	setValue?: (value: number) => void;
	onIncrement?: (value: number) => void;
	onDecrement?: (value: number) => void;
	min?: number;
	max?: number;
	disabled?: boolean;
	size?: Sizes;
	textProps?: TextProps;
}

const Counter = (props: CounterProps) => {
	const {
		size = 'md',
		value,
		setValue,
		onIncrement,
		onDecrement,
		min,
		max,
		disabled,
		textProps,
		className,
		'aria-label': ariaLabel = 'Counter',
		...rest
	} = props;
	const [internalValue, setInternalValue] = React.useState<string | null>(null);

	const handleIncrement = () => {
		if (max !== undefined && value >= max) return;
		const newValue = value + COUNTER_STEP;
		setValue?.(newValue);
		onIncrement?.(newValue);
	};

	const handleDecrement = () => {
		if (min !== undefined && value <= min) return;
		const newValue = value - COUNTER_STEP;
		setValue?.(newValue);
		onDecrement?.(newValue);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.value === '') {
			setInternalValue('');
			return;
		}

		const typedValue = Number(event.target.value);
		if (
			Number.isNaN(typedValue) ||
			(min !== undefined && typedValue < min) ||
			(max !== undefined && typedValue > max) ||
			typedValue % COUNTER_STEP !== 0
		)
			return;

		setInternalValue(event.target.value);
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		if (event.target.value === '') {
			const minValue = min && min > 0 ? min : 0;
			setValue?.(minValue);
			setInternalValue(minValue.toString());
			return;
		}
		const newValue = Number(event.target.value);
		if (newValue !== value) {
			setValue?.(newValue);
		}
	};

	React.useEffect(() => {
		setInternalValue(null);
	}, [value]);

	const displayValue = internalValue ?? value;

	return (
		<div
			aria-label={ariaLabel}
			className={cn('flex items-center justify-center gap-1.5', className)}
			{...rest}
		>
			<Button
				variant="tertiaryNew"
				size={size}
				iconButton
				onClick={handleDecrement}
				disabled={disabled || (min !== undefined && value <= min)}
				aria-label={`Decrement ${ariaLabel}`}
			>
				<Deduct />
			</Button>
			<Input
				{...textProps}
				name={ariaLabel}
				className="w-(--chars)"
				style={{ '--chars': `calc(${displayValue.toString().length} * 1ch + 0.25ch)` } as React.CSSProperties}
				variant="naked"
				size={textProps?.size ?? size}
				aria-label={`${ariaLabel} value`}
				onBlur={handleBlur}
				onChange={handleChange}
				onKeyPress={e => e.key === 'Enter' && e.currentTarget.blur()}
				min={min}
				max={max}
				value={displayValue}
				disabled={disabled}
				autoComplete="off"
				inputMode="numeric"
				type="text"
				pattern="[0-9]*"
				textCenter
			/>
			<Button
				variant="tertiaryNew"
				size={size}
				iconButton
				onClick={handleIncrement}
				disabled={disabled || (max !== undefined && value >= max)}
				aria-label={`Increment ${ariaLabel}`}
			>
				<Add />
			</Button>
		</div>
	);
};

export default Counter;
